import React, { useState } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'

import { AutoCoderFramework } from '../../types/gotypes'
import MatchEvidence from './MatchEvidence'
import ExpandedMatchDetailsModal from './ExpandedMatchDetailsModal'
import AutoCoderStatusChip from './AutoCoderStatusChip'
import { RecordCodingBlock, RecordRow, normalizeRecordCodings } from '../../utils/recordCodings'
import PlatformAttribution, { DeletedAtSourceNotice } from '../social/PlatformAttribution'

interface DataTableProps {
  data: RecordRow[]
  totalCount: number
  page: number
  rowsPerPage: number
  frameworks?: AutoCoderFramework[]                        // Single pipeline mode
  frameworksMap?: Record<string, AutoCoderFramework[]>    // Multi-pipeline mode
  colorMap?: Record<string, string>
  showState?: boolean
  evidenceLabel?: string
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
}

const DataExplorer: React.FC<DataTableProps> = ({
  data,
  totalCount,
  page,
  rowsPerPage,
  frameworks = [],
  frameworksMap,
  colorMap,
  showState = false,
  evidenceLabel = 'Match Evidence',
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null)

  // Helper to resolve the framework a single coding was produced with. The map
  // wins when it knows the pipeline — a stacked record is coded by several, and
  // each block must be read through its OWN framework — and the single-pipeline
  // `frameworks` prop is the fallback for the callers that pass only that.
  const getFrameworksForPipeline = (pipelineId: string): AutoCoderFramework[] => {
    if (frameworksMap && pipelineId && frameworksMap[pipelineId]) {
      return frameworksMap[pipelineId]
    }
    if (frameworks && frameworks.length > 0) return frameworks
    return []
  }

  const handleViewDetails = (recordId: string) => {
    setExpandedRecordId(recordId)
  }

  const handleCloseDetails = () => {
    setExpandedRecordId(null)
  }

  // One coding: its evidence when it is completed, otherwise the state it is
  // in. A queued coding has no answer yet, so rendering an empty framework
  // would read as "coded, found nothing".
  const renderBlock = (block: RecordCodingBlock, recordId: string, sourceText: string, textRemoved = false) => (
    // Addressed by (record, pipeline): "does record R carry pipeline P's
    // coding?" is the question a test asks, and the pipeline id alone is not
    // enough — the same pipeline codes every record on the page, so it repeats
    // across rows.
    //
    // It is the LEAST-BAD composite, not a unique key, and deliberately so.
    // The coding row's own id is the only truly unique identifier and it is NOT
    // on the wire: `AutoCoderRecordCodingView` carries pipeline_id but no id.
    // Two consequences a test author must know:
    //   - a RECODE is a new coding row (000095: there is no unique
    //     (record_id, pipeline_id)), so a record coded twice by one pipeline
    //     renders two blocks under this same testid — take the last one, which
    //     is the order `/records/query` returns;
    //   - `pipelineId` is '' on a LEGACY block of a detached record, so the
    //     block's `source` stands in and the id never ends bare.
    // The block key would settle both, but it interpolates the coding's
    // position in the list — the index this wave exists to remove.
    <Box
      key={block.key}
      data-testid={`autocoder-record-coding-${recordId}-${block.pipelineId || block.source}`}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
    >
      {block.label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
            {block.label}
          </Typography>
          {!block.isCoded && (
            <AutoCoderStatusChip state={block.state} error={block.error} />
          )}
        </Box>
      )}
      {block.isCoded ? (
        <MatchEvidence
          record={{ interview_id: '', source_text: sourceText, matches: [], suggestions: [] }}
          matches={block.matches}
          frameworks={getFrameworksForPipeline(block.pipelineId)}
          colorMap={colorMap}
          showLoopedVariables={false}
          showTooltips={false}
          textRemoved={textRemoved}
        />
      ) : (
        !block.label && <AutoCoderStatusChip state={block.state} error={block.error} />
      )}
    </Box>
  )

  const renderTableBody = () => {
    if (data.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={showState ? 4 : 3}>
            <Typography variant='body2' color='text.secondary'>No records available.</Typography>
          </TableCell>
        </TableRow>
      )
    }

    return data.map(row => {
      const normalized = normalizeRecordCodings(row)
      return (
        <TableRow
          key={normalized.id || normalized.interviewId}
          hover
          data-testid={`autocoder-record-row-${normalized.id || normalized.interviewId}`}
        >
          {/* 🔴 THIS CELL USED TO OVERFLOW INTO THE NEXT COLUMN. A hard
              160px width with no overflow rule, holding ids like
              INSTAGRAM_37_1810812972501268 — MUI does not clip by default, so
              the id ran straight over the evidence text and both became
              unreadable. break-all makes it wrap INSIDE its cell; nothing is
              hidden, which matters because the id is how an operator finds the
              message in Sprinklr.

              The subtitle carries what the row was silently withholding:
              platform, campaign and the comment's own date were all on the
              wire already and none of them were on screen. Campaign especially
              — it is the field that decides which Sky department a comment
              belongs to. */}
          <TableCell sx={{ width: 200, minWidth: 200, maxWidth: 200, verticalAlign: 'top', py: 1 }}>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all', display: 'block', lineHeight: 1.35 }}
            >
              {normalized.interviewId}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
              {row.metadata?.platform && (
                <Chip size="small" variant="outlined" label={row.metadata.platform} sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
              {/* `unknown` is shown, not hidden: "we do not know this comment's
                  campaign" is the single most useful thing this table can say
                  about a Sprinklr record, because it is exactly the state that
                  keeps it out of every department dashboard. */}
              {row.campaignId && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={row.campaignId === 'unknown' ? 'warning' : 'default'}
                  label={row.campaignId}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Box>
            {row.source_date && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {new Date(row.source_date as unknown as string).toISOString().slice(0, 16).replace('T', ' ')} UTC
              </Typography>
            )}
            {row.metadata?.platform && (
              <Box sx={{ mt: 0.5 }}>
                <PlatformAttribution platform={row.metadata.platform} url={row.metadata.permalink} />
              </Box>
            )}
          </TableCell>
          {
            showState && (
              <TableCell sx={{ width: 200, verticalAlign: 'middle' }}>
                {row.state === 'failed' && row.error ? (
                  <Tooltip title={row.error} arrow>
                    <AutoCoderStatusChip state={row.state} />
                  </Tooltip>
                ) : (
                  <AutoCoderStatusChip state={row.state} />
                )}
              </TableCell>
            )
          }
          <TableCell sx={{ verticalAlign: 'middle' }}>
            {row.metadata?.deleted_at_source && (
              // The source deleted this comment: its text is gone and its codes
              // stay, so say so in place of the text.
              <DeletedAtSourceNotice platform={row.metadata.platform || ''} deletedAt={row.metadata.deleted_at_source} />
            )}
            {normalized.blocks.length === 0 ? (
              // Uncoded: the record IS its text.
              row.metadata?.deleted_at_source ? null : (
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {normalized.sourceText || (
                    <Typography component='span' variant='body2' color='text.secondary'>
                      No source text available.
                    </Typography>
                  )}
                </Typography>
              )
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {normalized.blocks.map(block => renderBlock(block, normalized.id || normalized.interviewId, normalized.sourceText, Boolean(row.metadata?.deleted_at_source)))}
              </Box>
            )}
          </TableCell>
          <TableCell sx={{ width: 80, textAlign: 'center', verticalAlign: 'middle' }}>
            <IconButton
              size="small"
              onClick={() => handleViewDetails(row.id)}
              title="View details"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
      )
    })
  }

  const expandedRecord = data.find(record => record.id === expandedRecordId) || null

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table size='small' stickyHeader data-testid='autocoder-records-table'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 200, minWidth: 200, maxWidth: 200 }}>Record</TableCell>
                {
                  showState && (
                    <TableCell width='200px'>State</TableCell>
                  )
                }
                <TableCell width='100%'>{evidenceLabel}</TableCell>
                <TableCell sx={{ width: 80, textAlign: 'center' }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderTableBody()}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={totalCount ?? data.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, page) => onPageChange(page)}
          onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
        />
      </Box>
      <ExpandedMatchDetailsModal
        open={expandedRecordId !== null}
        record={expandedRecord}
        frameworks={frameworks}
        frameworksMap={frameworksMap}
        colorMap={colorMap}
        onClose={handleCloseDetails}
      />
    </>
  )
}

export default DataExplorer
